import type { Space } from '@cocoonspace/sdk-js/types/space'
import { memo, useMemo } from 'react'
import type { DayContentProps } from 'react-day-picker'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	Calendar,
	type CalendarProps,
} from '@cocoonspace/ui/components/calendar'
import { useLingui } from '@lingui/react/macro'
import { fr } from 'date-fns/locale/fr'

import { useAuth } from '~/providers/auth-provider'
import { BOOKING_MAX_DATE } from '../booking-stepper'
import { StepperHeader } from '../stepper-header'
import { DayProvider, useDayContext } from './provider'

const locales = {
	fr,
}

export const styles: CalendarProps['classNames'] = {
	button:
		'!text-sm rounded-full h-[45px] w-[45px] flex items-center justify-center cursor-pointer',
	cell: 'p-[1px] h-[50px] w-[50px]',
	head_cell: 'text-sm h-[30px] !w-[30px] font-normal opacity-50 p-1',
	month: 'px-4',
	table: 'mx-0 w-full mt-2',
}

const DayContent = memo(({ date }: DayContentProps) => {
	const { i18n } = useLingui()
	const { pricesQuery } = useDayContext()

	const price = useMemo(() => {
		const currentDay = dateTimeUtils(date).customFormat('dateTech')
		const currentPrice = pricesQuery.data?.pricesByDay[currentDay]
		return currentPrice?.price_hour_discounted ?? currentPrice?.price_hour
	}, [pricesQuery.data?.pricesByDay, date])

	const isFull = Number(price?.toString().slice(-2))

	return (
		<div className='flex flex-col gap-y-1 leading-none'>
			{date.getDate()}

			{price !== undefined && (
				<div className='text-xxs leading-none'>
					{i18n.number(price / 100, {
						style: 'currency',
						currency: 'EUR',

						...(!isFull && {
							minimumFractionDigits: 0,
							maximumFractionDigits: 2,
							trailingZeroDisplay: 'auto',
						}),
					})}
				</div>
			)}
		</div>
	)
})

export const StepDayContent = () => {
	const { isAuth } = useAuth()
	const { t } = useLingui()
	const { i18n } = useLingui()

	const { value, onChange, onClickBack, pricesQuery } = useDayContext()

	return (
		<div
			className='max-h-[512px] w-full overflow-x-hidden overflow-y-scroll overscroll-contain pb-4'
			data-testid='step-day'
		>
			<StepperHeader onClickBack={onClickBack}>
				{t`Date et horaires`}
			</StepperHeader>

			<Calendar
				classNames={styles}
				components={isAuth ? { DayContent: DayContent as any } : undefined}
				disabled={pricesQuery.data?.disabled}
				disableNavigation
				fromDate={dateTimeUtils().toDate()}
				locale={locales[i18n.locale as 'fr']}
				mode='single'
				numberOfMonths={13}
				onSelect={(day) => {
					onChange(dateTimeUtils(day).customFormat('dateTech'))
				}}
				selected={value ? new Date(value) : undefined}
				toDate={dateTimeUtils(BOOKING_MAX_DATE).toDate()}
			/>
		</div>
	)
}

export const StepDay = memo(
	({
		space,
		value,
		onChange,
		onClickBack,
	}: {
		space: Space
		value?: string
		onChange: (day: string) => void
		onClickBack: () => void
	}) => (
		<DayProvider
			onChange={onChange}
			onClickBack={onClickBack}
			space={space}
			value={value}
		>
			<StepDayContent />
		</DayProvider>
	),
)

StepDay.displayName = 'StepDay'
